/**
 * State + thunks for the Org Settings → Teams tab.
 *
 * Talks exclusively to the new /api/org/:orgId/team/* endpoints
 * (server enforces RBAC; this duck is just a transport).
 *
 * Spec: UPDATE.md §3.6.8 / §3.6.9
 */

import { orgTeamAPI } from '../../api';
import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { fetchOrganizationProfiles } from '../../ducks/organizations.duck';

// ================ Action types ================ //

export const FETCH_TEAM_REQUEST = 'app/OrgSettingsTeamsPage/FETCH_TEAM_REQUEST';
export const FETCH_TEAM_SUCCESS = 'app/OrgSettingsTeamsPage/FETCH_TEAM_SUCCESS';
export const FETCH_TEAM_ERROR = 'app/OrgSettingsTeamsPage/FETCH_TEAM_ERROR';

export const INVITE_REQUEST = 'app/OrgSettingsTeamsPage/INVITE_REQUEST';
export const INVITE_SUCCESS = 'app/OrgSettingsTeamsPage/INVITE_SUCCESS';
export const INVITE_ERROR = 'app/OrgSettingsTeamsPage/INVITE_ERROR';

export const REVOKE_INVITE_REQUEST = 'app/OrgSettingsTeamsPage/REVOKE_INVITE_REQUEST';
export const REVOKE_INVITE_SUCCESS = 'app/OrgSettingsTeamsPage/REVOKE_INVITE_SUCCESS';
export const REVOKE_INVITE_ERROR = 'app/OrgSettingsTeamsPage/REVOKE_INVITE_ERROR';

export const CHANGE_ROLE_REQUEST = 'app/OrgSettingsTeamsPage/CHANGE_ROLE_REQUEST';
export const CHANGE_ROLE_SUCCESS = 'app/OrgSettingsTeamsPage/CHANGE_ROLE_SUCCESS';
export const CHANGE_ROLE_ERROR = 'app/OrgSettingsTeamsPage/CHANGE_ROLE_ERROR';

export const REMOVE_MEMBER_REQUEST = 'app/OrgSettingsTeamsPage/REMOVE_MEMBER_REQUEST';
export const REMOVE_MEMBER_SUCCESS = 'app/OrgSettingsTeamsPage/REMOVE_MEMBER_SUCCESS';
export const REMOVE_MEMBER_ERROR = 'app/OrgSettingsTeamsPage/REMOVE_MEMBER_ERROR';

// ================ Reducer ================ //

const initialState = {
  orgId: null,
  members: [],
  pendingInvites: [],
  viewerRole: null,
  fetchInProgress: false,
  fetchLoaded: false,
  fetchError: null,
  inviteInProgress: false,
  inviteError: null,
  inviteSentEmail: null,
  revokeInProgress: null,
  revokeError: null,
  changeRoleInProgress: null,
  changeRoleError: null,
  removeInProgress: null,
  removeError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_TEAM_REQUEST:
      // On an org switch (different :orgId, same mounted component) drop the
      // previous org's members so they can't flash under the new org during
      // its fetch. Same orgId → keep them (stale-while-revalidate).
      return payload && payload.orgId !== state.orgId
        ? { ...initialState, orgId: payload.orgId, fetchInProgress: true }
        : { ...state, fetchInProgress: true, fetchError: null };
    case FETCH_TEAM_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        fetchLoaded: true,
        orgId: payload.orgId,
        members: payload.members || [],
        pendingInvites: payload.pendingInvites || [],
        viewerRole: payload.viewerRole || null,
      };
    case FETCH_TEAM_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };

    case INVITE_REQUEST:
      return { ...state, inviteInProgress: true, inviteError: null, inviteSentEmail: null };
    case INVITE_SUCCESS:
      return { ...state, inviteInProgress: false, inviteSentEmail: payload.email };
    case INVITE_ERROR:
      return { ...state, inviteInProgress: false, inviteError: payload };

    case REVOKE_INVITE_REQUEST:
      return { ...state, revokeInProgress: payload.email, revokeError: null };
    case REVOKE_INVITE_SUCCESS:
      return { ...state, revokeInProgress: null };
    case REVOKE_INVITE_ERROR:
      return { ...state, revokeInProgress: null, revokeError: payload };

    case CHANGE_ROLE_REQUEST:
      return { ...state, changeRoleInProgress: payload.userId, changeRoleError: null };
    case CHANGE_ROLE_SUCCESS:
      return { ...state, changeRoleInProgress: null };
    case CHANGE_ROLE_ERROR:
      return { ...state, changeRoleInProgress: null, changeRoleError: payload };

    case REMOVE_MEMBER_REQUEST:
      return { ...state, removeInProgress: payload.userId, removeError: null };
    case REMOVE_MEMBER_SUCCESS:
      return { ...state, removeInProgress: null };
    case REMOVE_MEMBER_ERROR:
      return { ...state, removeInProgress: null, removeError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchTeamRequest = orgId => ({ type: FETCH_TEAM_REQUEST, payload: { orgId } });
const fetchTeamSuccess = payload => ({ type: FETCH_TEAM_SUCCESS, payload });
const fetchTeamError = e => ({ type: FETCH_TEAM_ERROR, payload: e });

const inviteRequest = () => ({ type: INVITE_REQUEST });
const inviteSuccess = email => ({ type: INVITE_SUCCESS, payload: { email } });
const inviteErrorAction = e => ({ type: INVITE_ERROR, payload: e });

const revokeRequest = email => ({ type: REVOKE_INVITE_REQUEST, payload: { email } });
const revokeSuccess = () => ({ type: REVOKE_INVITE_SUCCESS });
const revokeErrorAction = e => ({ type: REVOKE_INVITE_ERROR, payload: e });

const changeRoleRequestAction = userId => ({ type: CHANGE_ROLE_REQUEST, payload: { userId } });
const changeRoleSuccess = () => ({ type: CHANGE_ROLE_SUCCESS });
const changeRoleErrorAction = e => ({ type: CHANGE_ROLE_ERROR, payload: e });

const removeRequestAction = userId => ({ type: REMOVE_MEMBER_REQUEST, payload: { userId } });
const removeSuccess = () => ({ type: REMOVE_MEMBER_SUCCESS });
const removeErrorAction = e => ({ type: REMOVE_MEMBER_ERROR, payload: e });

// ================ Thunks ================ //

const fetchTeam = orgId => async dispatch => {
  dispatch(fetchTeamRequest(orgId));
  try {
    const response = await orgTeamAPI.list(orgId);
    // axios -> response.data is the HTTP body; server wraps body in { data: ... }
    const body = response?.data ?? response ?? {};
    const data = body?.data ?? body;
    dispatch(
      fetchTeamSuccess({
        orgId,
        members: data.teamMembers || [],
        pendingInvites: data.pendingInvites || [],
        viewerRole: data.currentUserRole || null,
      })
    );
    return data;
  } catch (e) {
    dispatch(fetchTeamError(storableError(e)));
    throw e;
  }
};

export const fetchTeamMembers = orgId => fetchTeam(orgId);

export const inviteOrgMember = (orgId, { email, role }) => async dispatch => {
  dispatch(inviteRequest());
  try {
    await orgTeamAPI.invite(orgId, { email, role });
    dispatch(inviteSuccess(email));
    await dispatch(fetchTeam(orgId));
    return true;
  } catch (e) {
    dispatch(inviteErrorAction(storableError(e)));
    throw e;
  }
};

export const revokeOrgInvite = (orgId, email) => async dispatch => {
  dispatch(revokeRequest(email));
  try {
    await orgTeamAPI.revokeInvite(orgId, { email });
    dispatch(revokeSuccess());
    await dispatch(fetchTeam(orgId));
    return true;
  } catch (e) {
    dispatch(revokeErrorAction(storableError(e)));
    throw e;
  }
};

export const changeOrgMemberRole = (orgId, userId, newRole) => async dispatch => {
  dispatch(changeRoleRequestAction(userId));
  try {
    await orgTeamAPI.changeRole(orgId, userId, { newRole });
    dispatch(changeRoleSuccess());
    await dispatch(fetchTeam(orgId));
    return true;
  } catch (e) {
    dispatch(changeRoleErrorAction(storableError(e)));
    throw e;
  }
};

export const removeOrgMember = (orgId, userId) => async dispatch => {
  dispatch(removeRequestAction(userId));
  try {
    await orgTeamAPI.removeMember(orgId, userId);
    dispatch(removeSuccess());
    // The viewer might have just removed themself; refresh user too.
    await dispatch(fetchCurrentUser());
    await dispatch(fetchTeam(orgId));
    return true;
  } catch (e) {
    dispatch(removeErrorAction(storableError(e)));
    throw e;
  }
};

// ================ loadData ================ //

export const loadData = (params, search) => async dispatch => {
  const orgId = params?.orgId;
  if (!orgId) return null;

  // Ensure the user + org switcher data is loaded so the page chrome works,
  // then fetch the org-scoped team roster.
  await dispatch(fetchCurrentUser());
  await dispatch(fetchOrganizationProfiles());
  return dispatch(fetchTeam(orgId));
};
